/**
 * CopyDayDialog Component
 * Dialog for copying steps from one day to multiple other days
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormControlLabel,
  FormGroup,
  Checkbox,
  Typography,
  Box,
  Switch,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DayOfWeek, ALL_DAYS_OF_WEEK } from '../../types/schedule';

interface CopyDayDialogProps {
  open: boolean;
  sourceDay: DayOfWeek;
  onClose: () => void;
  onCopy: (targetDays: DayOfWeek[], clearExisting: boolean) => Promise<void>;
}

export const CopyDayDialog: React.FC<CopyDayDialogProps> = ({
  open,
  sourceDay,
  onClose,
  onCopy,
}) => {
  const { t } = useTranslation();
  const [selectedDays, setSelectedDays] = useState<Set<DayOfWeek>>(new Set());
  const [clearExisting, setClearExisting] = useState(true);
  const [loading, setLoading] = useState(false);

  const availableDays = ALL_DAYS_OF_WEEK.filter((day) => day !== sourceDay);

  const handleDayToggle = (day: DayOfWeek) => {
    setSelectedDays((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(day)) {
        newSet.delete(day);
      } else {
        newSet.add(day);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedDays.size === availableDays.length) {
      // Deselect all
      setSelectedDays(new Set());
    } else {
      // Select all
      setSelectedDays(new Set(availableDays));
    }
  };

  const handleCopy = async () => {
    if (selectedDays.size === 0) return;

    setLoading(true);
    try {
      await onCopy(Array.from(selectedDays), clearExisting);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedDays(new Set());
      setClearExisting(true);
      onClose();
    }
  };

  const allSelected = selectedDays.size === availableDays.length;
  const someSelected = selectedDays.size > 0 && selectedDays.size < availableDays.length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('schedules.weekly.copyDayTitle')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('schedules.weekly.copyDayDescription')}
        </Typography>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('schedules.weekly.copyFrom')}: <strong>{t(`schedules.daysLong.${sourceDay}`)}</strong>
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2">
            {t('schedules.weekly.copyTo')}:
          </Typography>
          <Button size="small" onClick={handleSelectAll}>
            {allSelected ? t('common.deselectAll') : t('common.selectAll')}
          </Button>
        </Box>

        <FormControl component="fieldset" fullWidth>
          <FormGroup>
            {availableDays.map((day) => (
              <FormControlLabel
                key={day}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedDays.has(day)}
                    onChange={() => handleDayToggle(day)}
                  />
                }
                label={t(`schedules.daysLong.${day}`)}
              />
            ))}
          </FormGroup>
        </FormControl>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={clearExisting}
                onChange={(e) => setClearExisting(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                {clearExisting
                  ? t('schedules.weekly.clearExisting')
                  : t('schedules.weekly.appendSteps')}
              </Typography>
            }
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleCopy}
          disabled={selectedDays.size === 0 || loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {t('schedules.weekly.copySteps')}
          {selectedDays.size > 0 && ` (${selectedDays.size})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopyDayDialog;
